import {
  domSnapshotListResponseSchema,
  dumpCoverageResponseSchema,
  rollbackSelectorPackResponseSchema,
  selectorHealthResponseSchema,
  selectorPackDetailResponseSchema,
  selectorPackListResponseSchema,
  selectorPackPublishResultSchema,
  type DomSnapshotItem,
  type DumpCoverageResponse,
  type PaginationMeta,
  type SelectorHealthItem,
  type SelectorHealthResponse,
  type SelectorHealthUpdated,
  type SelectorPackDetailResponse,
  type SelectorPackSummary,
} from '@midiautolistening/contracts';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { ApiRequestError, apiGet, apiPost, toApiError } from '@/lib/api';

/**
 * SelectorPacks, their versions, selector health and uploaded DOM snapshots.
 *
 * A pack is *versioned declarative data*, never code: publishing sends the
 * document to agents over `selector_pack.updated` and a fixed interpreter reads
 * it. That is why the publish form takes JSON validated by
 * `selectorPackDocumentSchema` — a document containing anything outside the closed
 * strategy enums (a JavaScript string, above all) is rejected here before it can
 * reach a single client machine.
 */

export const useSelectorPacksStore = defineStore('selectorPacks', () => {
  const packs = ref<SelectorPackSummary[]>([]);
  const packsMeta = ref<PaginationMeta | null>(null);
  const packsLoading = ref(false);
  const packsError = ref<ApiRequestError | null>(null);

  const detail = ref<SelectorPackDetailResponse | null>(null);
  const detailLoading = ref(false);
  const detailError = ref<ApiRequestError | null>(null);

  const health = ref<SelectorHealthItem[]>([]);
  const healthSummary = ref<SelectorHealthResponse['summary'] | null>(null);
  const healthLoading = ref(false);
  const healthError = ref<ApiRequestError | null>(null);
  const onlyFailing = ref(true);

  const snapshots = ref<DomSnapshotItem[]>([]);
  const snapshotsLoading = ref(false);
  const snapshotsError = ref<ApiRequestError | null>(null);

  const submitting = ref(false);
  /** Health events for rules outside the current table view — "bảng đang cũ". */
  const staleHealthCount = ref(0);

  /** Kết quả đối chiếu Dump DOM gần nhất; chỉ sống trong phiên xem trang. */
  const dumpReport = ref<DumpCoverageResponse | null>(null);
  const dumpAnalyzing = ref(false);
  const dumpError = ref<ApiRequestError | null>(null);

  /**
   * Adapter generations seen in the field, derived from selector-health events and
   * snapshots. The pack screen surfaces it because a pack that needs a newer
   * `minAdapterVersion` than an agent ships is a pack that agent will refuse.
   */
  const observedAdapterVersions = computed<number[]>(() => {
    const versions = new Set<number>();
    for (const item of health.value) {
      if (item.adapterVersion !== null) versions.add(item.adapterVersion);
    }
    for (const snapshot of snapshots.value) versions.add(snapshot.adapterVersion);
    return [...versions].sort((left, right) => left - right);
  });

  async function fetchPacks(): Promise<void> {
    packsLoading.value = true;
    packsError.value = null;
    try {
      const response = await apiGet('/selector-packs', selectorPackListResponseSchema, {
        page: 1,
        pageSize: 50,
      });
      packs.value = response.items;
      packsMeta.value = response.meta;
    } catch (caught) {
      packsError.value = toApiError(caught);
      packs.value = [];
      packsMeta.value = null;
    } finally {
      packsLoading.value = false;
    }
  }

  async function fetchPackDetail(packId: string): Promise<void> {
    detailLoading.value = true;
    detailError.value = null;
    try {
      detail.value = await apiGet(
        `/selector-packs/${packId}`,
        selectorPackDetailResponseSchema,
      );
    } catch (caught) {
      detailError.value = toApiError(caught);
      detail.value = null;
    } finally {
      detailLoading.value = false;
    }
  }

  async function publishVersion(
    packId: string,
    body: { versionId?: string; document?: unknown; changelog?: string; minAdapterVersion?: number },
  ): Promise<void> {
    submitting.value = true;
    try {
      await apiPost(
        `/selector-packs/${packId}/publish`,
        selectorPackPublishResultSchema,
        body,
      );
      await Promise.all([fetchPackDetail(packId), fetchPacks()]);
    } finally {
      submitting.value = false;
    }
  }

  async function rollback(
    packId: string,
    body: { versionId?: string; versionNumber?: number; reason?: string },
  ): Promise<void> {
    submitting.value = true;
    try {
      await apiPost(
        `/selector-packs/${packId}/rollback`,
        rollbackSelectorPackResponseSchema,
        body,
      );
      await Promise.all([fetchPackDetail(packId), fetchPacks()]);
    } finally {
      submitting.value = false;
    }
  }

  /**
   * Upload nội dung một file Dump DOM để server đếm mỗi field rule còn khớp bao
   * nhiêu card. `draftDocument` cho vòng lặp "sửa → thử lại" mà không phải
   * publish; bỏ trống thì đối chiếu với bản đang phát hành.
   */
  async function analyzeDump(
    packId: string,
    html: string,
    draftDocument?: unknown,
  ): Promise<void> {
    dumpAnalyzing.value = true;
    dumpError.value = null;
    dumpReport.value = null;
    try {
      dumpReport.value = await apiPost(
        `/selector-packs/${packId}/dump-coverage`,
        dumpCoverageResponseSchema,
        { html, ...(draftDocument === undefined ? {} : { document: draftDocument }) },
      );
    } catch (caught) {
      dumpError.value = toApiError(caught);
    } finally {
      dumpAnalyzing.value = false;
    }
  }

  async function fetchHealth(packId?: string): Promise<void> {
    healthLoading.value = true;
    healthError.value = null;
    try {
      const response = await apiGet('/selector-health', selectorHealthResponseSchema, {
        page: 1,
        pageSize: 100,
        packId: packId || undefined,
        onlyFailing: onlyFailing.value,
        sortOrder: 'desc',
      });
      health.value = response.items;
      healthSummary.value = response.summary;
    } catch (caught) {
      healthError.value = toApiError(caught);
      health.value = [];
      healthSummary.value = null;
    } finally {
      healthLoading.value = false;
    }
  }

  async function fetchSnapshots(): Promise<void> {
    snapshotsLoading.value = true;
    snapshotsError.value = null;
    try {
      const response = await apiGet('/dom-snapshots', domSnapshotListResponseSchema, {
        page: 1,
        pageSize: 50,
        sortOrder: 'desc',
      });
      snapshots.value = response.items;
    } catch (caught) {
      snapshotsError.value = toApiError(caught);
      snapshots.value = [];
    } finally {
      snapshotsLoading.value = false;
    }
  }

  /**
   * Realtime: `selector.health_updated`. Patches the row in place so an admin
   * watching the table sees a rule degrade without polling — that early warning is
   * the entire point of the health channel.
   */
  function applySelectorHealthUpdated(event: SelectorHealthUpdated): void {
    const index = health.value.findIndex((item) => item.ruleKey === event.ruleKey);
    if (index === -1) {
      // A rule we are not currently displaying started failing; the counter tells
      // the admin the table is stale rather than inventing a half-filled row.
      if (event.outcome !== 'SUCCESS') staleHealthCount.value += 1;
      return;
    }

    const current = health.value[index];
    const patched: SelectorHealthItem = {
      ...current,
      worstOutcome: event.outcome === 'SUCCESS' ? current.worstOutcome : event.outcome,
      failureCount: event.failureCount,
      failureRate: event.failureRate,
      lastSeenAt: event.occurredAt,
      packVersionNumber: event.packVersionNumber ?? current.packVersionNumber,
    };
    health.value = [
      ...health.value.slice(0, index),
      patched,
      ...health.value.slice(index + 1),
    ];
  }

  function clearStaleHealthCount(): void {
    staleHealthCount.value = 0;
  }

  function reset(): void {
    packs.value = [];
    packsMeta.value = null;
    packsError.value = null;
    detail.value = null;
    detailError.value = null;
    health.value = [];
    healthSummary.value = null;
    healthError.value = null;
    snapshots.value = [];
    snapshotsError.value = null;
    staleHealthCount.value = 0;
    dumpReport.value = null;
    dumpError.value = null;
  }

  return {
    packs,
    packsMeta,
    packsLoading,
    packsError,
    detail,
    detailLoading,
    detailError,
    health,
    healthSummary,
    healthLoading,
    healthError,
    onlyFailing,
    snapshots,
    snapshotsLoading,
    snapshotsError,
    submitting,
    staleHealthCount,
    dumpReport,
    dumpAnalyzing,
    dumpError,
    observedAdapterVersions,
    fetchPacks,
    fetchPackDetail,
    publishVersion,
    rollback,
    analyzeDump,
    fetchHealth,
    fetchSnapshots,
    applySelectorHealthUpdated,
    clearStaleHealthCount,
    reset,
  };
});
